import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { VehicleChargingFigureService } from 'src/app/_services/vehicle-charging-figure.service';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-soc-delta-per-charging-procedure-chart',
  templateUrl: './soc-delta-per-charging-procedure-chart.component.html',
  styleUrls: ['./soc-delta-per-charging-procedure-chart.component.scss']
})
export class SocDeltaPerChargingProcedureChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles!: any[];
  deviceSerialNumber!: any[];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  private colorMap: {[key:string]:string}={};
  chartRange!: { range: [number, number]; };

  constructor(
    private readonly vehicleChargingFigureService: VehicleChargingFigureService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    this.vehicleChargingFigureService.getSocDelta(this.deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data: any) => {
        this.plotsocdeltapercharging(data);
        this.dataLoading = false;
      },
      error: (error) => {
        console.error('Error fetching State of Charge Difference data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      },
      complete: () => {
        this.dataLoading = false;
      }
    })
  }

  plotsocdeltapercharging(data: any): void {
    this.initializeColorMap();
    const transformXAxisPipe = new TransformXAxisPipe();
    const plotData = Object.keys(data).flatMap((deviceSerialNumberId: string) => {
      const deviceDataArray = data[deviceSerialNumberId];
      const vehicle = this.selectedVehicles.find(vehicle => vehicle.batteryIdentifier === deviceSerialNumberId);
      const bin = deviceDataArray.map((dataObject: any) => dataObject.binSocDifference);
      const soc = deviceDataArray.map((dataObject: any) => dataObject.chargingSessions);
      this.chartRange = transformXAxisPipe.transform(bin, soc, 5,100);
  
      return {
        x: bin,
        y: soc,
        type: 'bar',
        name: vehicle ? vehicle.name: deviceSerialNumberId,
        marker: {
          color: this.colorMap[deviceSerialNumberId]
        }
      };
    });
  
    const layout = {
      title: 'State of Charge Difference per Charging Session',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'State of Charge Increase [%]',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Charging Sessions [%]'
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('soc-delta-per-charging-procedure', plotData, layout);    
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }
}
